import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserX, Loader2, ArrowRight, RefreshCw, CheckCircle2 } from "lucide-react";

const fmtMonth = (m) => new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1).toLocaleString("en", { month: "short", year: "numeric" });

const guessTarget = (item, users) => {
    const names = [...item.names, ...item.aliases].map((n) => (n || "").toLowerCase().split(/\s+/)[0]).filter(Boolean);
    return users.find((u) => names.includes((u.name || "").toLowerCase().split(/\s+/)[0]))?.user_id || "";
};

const OrphanCard = ({ item, users, onDone }) => {
    const [to, setTo] = useState(() => guessTarget(item, users));
    const [busy, setBusy] = useState(false);
    const target = users.find((u) => u.user_id === to);

    const reassign = async () => {
        if (!target) return;
        if (!window.confirm(`Move all of ${item.names[0] || "this person"}'s attendance history to ${target.name} (${target.email})?`)) return;
        setBusy(true);
        try {
            const r = await api.post("/admin/orphaned-attendance/reassign", { from_user_id: item.user_id, to_user_id: to });
            const m = r.data.moved, c = r.data.conflicts;
            const clash = c.attendance_records + c.attendance_message_log + c.kno_attendance;
            toast.success(`Moved ${m.attendance_records} months · ${m.attendance_message_log} messages · ${m.kno_attendance} KNO · ${m.attendance_name_aliases} name match${clash ? ` · ${clash} skipped (${target.name} already had them)` : ""}`);
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reassign failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={`orphan-${item.user_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{item.names.join(" / ") || "Unknown name"}</div>
                    <div className="text-[11px] text-slate-400 font-mono mt-0.5">deleted account · {item.user_id}</div>
                    <div className="flex flex-wrap gap-1.5 mt-3 text-xs">
                        <span className="px-2 py-0.5 rounded-full bg-teal-50 text-[#25908A] border border-teal-200 font-semibold">{item.attendance_records} month{item.attendance_records === 1 ? "" : "s"}</span>
                        <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200">{item.attendance_message_log} messages</span>
                        <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200">{item.kno_attendance} KNO</span>
                        {item.aliases.map((a) => (
                            <span key={a} className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">chat name “{a}”</span>
                        ))}
                    </div>
                    {item.months.length > 0 && <div className="text-xs text-slate-500 mt-2">{item.months.map(fmtMonth).join(" · ")}</div>}
                </div>
                <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-slate-300 hidden sm:block" />
                    <select
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        data-testid={`orphan-target-${item.user_id}`}
                        className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 max-w-[260px] focus:outline-none focus:border-[#2EB4AC]"
                    >
                        <option value="">Reassign to…</option>
                        {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {u.email}</option>)}
                    </select>
                    <button
                        onClick={reassign}
                        disabled={!to || busy}
                        data-testid={`orphan-reassign-${item.user_id}`}
                        className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold disabled:opacity-50 transition-colors"
                    >
                        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Reassign
                    </button>
                </div>
            </div>
        </div>
    );
};

export const OrphanedAttendance = ({ users = [], onChanged }) => {
    const [items, setItems] = useState(null);
    const sortedUsers = useMemo(() => [...users].filter((u) => u.active !== false).sort((a, b) => (a.name || "").localeCompare(b.name || "")), [users]);

    const load = async () => {
        setItems(null);
        try {
            const r = await api.get("/admin/orphaned-attendance");
            setItems(r.data.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load orphaned records");
            setItems([]);
        }
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-4" data-testid="orphaned-attendance">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 flex items-start justify-between gap-4">
                <div>
                    <h3 className="font-heading text-xl font-semibold text-slate-900 inline-flex items-center gap-2">
                        <UserX className="h-5 w-5 text-amber-500" /> Orphaned attendance
                    </h3>
                    <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                        Attendance, chat messages and KNO entries still attached to deleted accounts (e.g. after re-creating someone with a corrected email). Reassign them to the right person — their chat-name match is moved too, so future uploads land correctly.
                    </p>
                </div>
                <button onClick={load} data-testid="orphan-refresh" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:border-[#2EB4AC] hover:text-[#25908A] transition-colors">
                    <RefreshCw className="h-4 w-4" /> Refresh
                </button>
            </div>
            {items === null ? (
                <div className="h-24 rounded-xl bg-slate-100 animate-pulse" />
            ) : items.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500" data-testid="orphan-empty">
                    <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-2" /> No orphaned attendance — everything belongs to an active account.
                </div>
            ) : (
                items.map((it) => <OrphanCard key={it.user_id} item={it} users={sortedUsers} onDone={() => { load(); onChanged?.(); }} />)
            )}
        </div>
    );
};
